import type { Dictionary } from './types';

export const en: Dictionary = {
  common: {
    dashboard: 'Overview',
    apiKeys: 'API keys',
    usage: 'Usage',
    reports: 'Reports',
    models: 'Models',
    topUp: 'Credit Wallet',
    team: 'Team',
    playground: 'Playground',
    guide: 'Guide',
    signOut: 'Sign out',
    editProfile: 'Edit Profile',
    personal: 'Personal',
    backToWebsite: 'Back to the IC Lueur Ai website',
    skipToContent: 'Skip to content',
    primaryNav: 'Primary',
    cancel: 'Cancel',
    close: 'Close',
    working: 'Working…',
    never: 'Never',
    previous: 'Previous',
    next: 'Next',
    loadMore: 'Load more',
    loading: 'Loading…',
    clear: 'Clear',
    pageOf: (page, totalPages) => `Page ${page} of ${totalPages}`,
    itemCount: (n) => `${n.toLocaleString()} item${n === 1 ? '' : 's'}`,
    showingSoFar: (n) => `Showing ${n.toLocaleString()}`,
    pageNumber: (page) => `Page ${page}`,
    rowsPerPage: 'Rows per page',
    goToPage: (page) => `Go to page ${page}`,
    otpDidntGetCode: "Didn't get the code?",
    otpResendLink: 'Click to resend',
    otpDigitLabel: (digit, total) => `Digit ${digit} of ${total}`,
    orgSwitcherLabel: 'Organization',
    datePicker: {
      today: 'Today',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      weekdays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      months: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ],
    },
  },
  notifications: {
    bellLabel: 'Notifications',
    panelHeading: 'Notifications',
    empty: 'No notifications yet.',
    loadError: 'Could not load notifications.',
    viewAllLink: 'View all notifications',
    historyHeading: 'Notifications',
    historyDescription: 'Everything sent to you, newest first.',
    filterLabel: 'Filter by type',
    filterAll: 'All types',
    typeLabel: (type, isB2B) => {
      switch (type) {
        case 'key_suspended_abuse':
          return 'API key suspended';
        case 'low_balance':
          return 'Low balance';
        case 'free_quota_fallback_billing':
          return 'Free quota used up';
        case 'topup_requested':
          return 'Top-up requested';
        case 'added_to_org':
          return isB2B ? 'Added to organization' : 'Added to team';
        default:
          return type;
      }
    },
    actionGoToPage: 'View',
    actionContactSupport: 'Contact support',
  },
  themeToggle: {
    toLight: 'Switch to light theme',
    toDark: 'Switch to dark theme',
  },
  languageSwitcher: {
    switchToEnglish: 'Switch to English',
    switchToThai: 'Switch to Thai',
    english: 'EN',
    thai: 'TH',
  },
  dashboard: {
    subtitle: 'Balance, activity, and a few useful next steps.',
    greetingMorning: (name) => `Good morning, ${name}`,
    greetingAfternoon: (name) => `Good afternoon, ${name}`,
    greetingEvening: (name) => `Good evening, ${name}`,
    creditBalance: 'Credit balance',
    depleted:
      'Your credit balance is used up. API requests cannot be processed until you top up your credit.',
    unfunded: 'No credit available. Top up credit to start using the service.',
    prepaidNote: 'Prepaid credit. Unused credit stays yours — it is not consumed by time.',
    topUpCta: 'Top up credit',
    balanceRestricted: (isB2B) =>
      isB2B ? 'Only admins and owners can view the org balance.' : 'Only admins and owners can view the account balance.',
    memberCreditSufficient: 'Credit available',
    memberCreditDepleted: 'Credit depleted',
    memberCreditUnfunded: 'No credit yet',
    memberCreditDepletedContact: 'Contact an admin or the owner to top up credit.',
    memberCreditUnfundedContact: 'Ask an admin or the owner to top up credit to get started.',
    requestTopUpCta: 'Request a top-up',
    requestTopUpSent: 'Request sent — an admin has been notified.',
    requestTopUpCooldown: 'Already asked recently — try again later.',
    requestTopUpError: 'Could not send the request. Try again.',
    requestTopUpSendAll: 'Notify everyone (admins + owner)',
    requestTopUpSendSpecific: 'Choose a specific person',
    requestTopUpPickPlaceholder: 'Select a person…',
    requestTopUpSendTo: 'Send',
    recentUsage: 'Recent usage',
    viewAll: 'View all',
    colWhen: 'When',
    colModel: 'Model',
    colIn: 'In',
    colOut: 'Out',
    colCost: 'Cost (THB)',
    emptyTitle: 'No requests yet',
    emptyBody: 'Create an API key and make your first call to see it here.',
    emptyCta: 'Create an API key',
    statusInterrupted: 'Interrupted',
    statusFailed: 'Failed',
    loadError: 'Could not load recent usage.',
    quickLinks: {
      heading: 'Shortcuts',
      keysTitle: 'API keys',
      keysBody: 'Create and manage the keys your applications call with.',
      usageTitle: 'Usage',
      usageBody: 'Spend over time, broken down by day, model or key.',
      modelsTitle: 'Models',
      modelsBody: 'Every model on the platform and what it costs to call.',
      topupTitle: 'Top up',
      topupBody: 'Add prepaid credit by PromptPay.',
    },
    activity: {
      heading: 'Activity',
      loadError: 'Could not load activity.',
      loading: 'Loading activity…',
      emptyTitle: 'No activity yet',
      emptyBody: 'Spend will show up here once you start calling the API.',
      summary: (total, streakDays) =>
        `Activity over the past year. Total spend ${total}. Longest streak ${streakDays} day${streakDays === 1 ? '' : 's'}.`,
      longestStreak: 'Longest streak',
      days: (n) => `${n} day${n === 1 ? '' : 's'}`,
      avgPerDay: 'Avg / day',
      avgPerWeek: 'Avg / week',
      total: 'Total',
      less: 'Less',
      more: 'More',
      weekdayMon: 'M',
      weekdayWed: 'W',
      weekdayFri: 'F',
    },
  },
  keys: {
    subtitle: 'Keys authenticate your applications against the gateway.',
    createHeading: 'Create a key',
    viewSetupGuide: 'View setup guide',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. production-server',
    noteLabel: 'Note',
    notePlaceholder: 'Optional — e.g. used by billing-worker',
    creating: 'Creating…',
    createButton: 'Create key',
    createError: 'Could not create a key.',
    yourKeys: 'Your keys',
    listError: 'Could not load your keys.',
    colName: 'Name',
    colPrefix: 'Prefix',
    colRouting: 'Routing',
    colStatus: 'Status',
    colCreated: 'Created',
    colLastUsed: 'Last used',
    neverUsed: 'Never used',
    colExpires: 'Expires',
    colNote: 'Note',
    sortLabel: 'Sort',
    sortOptions: {
      created_desc: 'Newest first',
      expires_asc: 'Expires soonest',
      expires_desc: 'Expires latest',
    },
    actionsAria: 'Actions',
    routing: {
      strict: 'Strict — near region only',
      regional: 'Regional (default)',
      available: 'Available — any region',
    },
    status: {
      active: 'Active',
      revoked: 'Revoked',
      expired: 'Expired',
    },
    revoke: 'Revoke',
    detail: 'Detail',
    detailTitle: (name) => name,
    detailPrefixNote:
      'The full secret is shown only once at creation. This prefix identifies the key in lists — it is not enough to authenticate.',
    colRateLimit: 'Rate limit',
    rateLimitUnlimited: 'No override (gateway default)',
    rateLimitPerMinute: (n) => `${n} requests / minute`,
    edit: 'Edit',
    editTitle: (name) => `Key limit — ${name}`,
    rateLimitLabel: 'Requests per minute',
    rateLimitHelp:
      "This limits how fast the key can be called, not how long it stays valid — that's controlled separately by Expiration below.",
    rateLimitUnlimitedLabel: 'No limit (use the gateway default)',
    rateLimitPlaceholder: 'e.g. 60',
    editSave: 'Save',
    editSaving: 'Saving…',
    editError: 'Could not update the rate limit.',
    expiresLabel: 'Expiration',
    neverExpires: 'Never expires',
    expiryPresetLabel: {
      none: 'No expiration',
      '1h': '1 hour',
      '1d': '1 day',
      '7d': '7 days',
      '30d': '30 days',
      '90d': '90 days',
      '180d': '180 days',
      '1y': '1 year',
    },
    expiryKeepCurrent: 'Keep current',
    expiryCurrentlyExpires: (when) => `Currently expires ${when}.`,
    expiryCurrentlyNever: 'Currently never expires.',
    emptyTitle: 'No API keys yet',
    emptyBody: 'Create one above to start calling the API.',
    revokeTitle: 'Revoke this key?',
    revokeBodySuffix: (prefix) =>
      ` (${prefix}…) will stop working immediately for new requests. Any response already streaming is allowed to finish.`,
    revokeBodyWarning: 'This cannot be undone. A revoked key cannot be reactivated — create a new one.',
    revokeConfirmLabel: 'Revoke key',
    revokeError: 'Could not revoke this key.',
    secretModalTitle: 'Save this key now',
    secretWarning: 'This secret is shown exactly once and cannot be recovered.',
    secretWarningBody:
      'We store only a hash of it — if you lose it, the only fix is to revoke this key and create a new one.',
    copy: 'Copy',
    copied: 'Copied',
    ackLabel: 'I have copied this key and saved it somewhere safe.',
    secretBaseUrlHint: (baseUrl: string) =>
      `Then point your Base URL to ${baseUrl}`,
    done: 'Done',
  },
  models: {
    subtitle: 'Every model on the platform, and what it costs to call.',
    loadError: 'Could not load the model catalogue.',
    searchLabel: 'Search',
    searchPlaceholder: 'Search models',
    providerLabel: 'Provider',
    allProviders: 'All providers',
    regionLabel: 'Region',
    allRegions: 'All regions',
    regionName: {
      'ap-southeast-7': 'Bangkok',
      'ap-southeast-1': 'Singapore',
      'ap-northeast-1': 'Tokyo',
      'ap-northeast-3': 'Osaka',
      global: 'Global',
    },
    modeLabel: 'Type',
    allModes: 'All types',
    modeChat: 'Chat',
    modeEmbedding: 'Embedding',
    modeImage: 'Image',
    modeVideo: 'Video',
    modalitiesLabel: 'Modalities',
    modalityText: 'Text',
    modalityAudio: 'Audio',
    modalityFile: 'File',
    sortLabel: 'Sort',
    sortInputAsc: 'Input price: low to high',
    sortInputDesc: 'Input price: high to low',
    sortOutputAsc: 'Output price: low to high',
    sortOutputDesc: 'Output price: high to low',
    sortNameAsc: 'Model name: A–Z',
    sortNameDesc: 'Model name: Z–A',
    emptyTitle: 'No models match your search',
    emptyBody: 'Try clearing the search or widening the provider / region filters.',
    input: 'Input',
    output: 'Output',
    perMillionTok: '/ 1M tok',
    noStreaming: 'No streaming',
    pageOf: (page, count) => `Page ${page} of ${count}`,
    modelCount: (n) => `${n} model${n === 1 ? '' : 's'}`,
    inputPrice: 'Input price',
    outputPrice: 'Output price',
    cacheRead: 'Cache read',
    cacheWrite: 'Cache write',
    contextWindow: 'Context window',
    tokensSuffix: 'tokens',
    streaming: 'Streaming',
    supported: 'Supported',
    notSupported: 'Not supported',
    medianTtft: 'Median TTFT',
    msFromBangkok: (ms) => `${ms} ms, measured from Bangkok`,
    medianThroughput: 'Median throughput',
    tokSecFromBangkok: (tps) => `${tps} tokens/sec, measured from Bangkok`,
    availableRegions: 'Available regions',
    freeBadge: 'Free to use',
    freeUntil: (date) => `Free until ${date} — no charge while this promotion runs.`,
    filterFree: 'Free to use',
    filterPromo: 'Has promotion',
    filterReset: 'Reset',
    promoBadge: (days) => `Promo: ${days} day${days === 1 ? '' : 's'} left`,
    showingAll: (n) => `Showing all ${n} model${n === 1 ? '' : 's'}`,
    matchingFilters: (n) => `Found ${n} model${n === 1 ? '' : 's'} matching filters`,
    quickStart: {
      title: 'Quick Start',
      subtitle: 'Drop-in code to call this model with our OpenAI-compatible API.',
      back: '← Back',
      tryThisModel: 'Try this model',
      step1Title: 'Get your API key',
      step1NoKey: 'Create an API key from the API Keys page and set it as an environment variable:',
      step1HasKey: 'Use an API key you have already created.',
      createKeyCta: 'Create API key',
      keysLoadError: 'Could not check whether you already have an API key. Open API Keys to create or review one.',
      keysPageCta: 'Open API Keys',
      envBash: 'Bash / zsh',
      envPowerShell: 'PowerShell',
      installLabel: 'Install the official OpenAI library first:',
      step2Title: 'Make your first request',
      step2IntroPrefix: 'Use',
      step2IntroSuffix: 'with the LLMR gateway:',
      tabCurl: 'cURL',
      tabPython: 'Python',
      tabNode: 'Node / TypeScript',
      gatewayUnavailable:
        'The gateway URL is not configured for this build — code snippets are unavailable.',
    },
  },
  authChatPreviews: {
    gpt: {
      user: 'Summarise this contract in bullet points.',
      assistant: 'Here are the key obligations, renewal terms, and liability caps…',
      placeholder: 'Ask anything',
    },
    claude: {
      user: 'Review this pull request for security issues.',
      assistant: 'I found two concerns: unsanitised input in the auth handler…',
      placeholder: 'How can I help you today?',
    },
    gemini: {
      user: 'Draft a product update email for our API customers.',
      assistant: 'Subject: New models and lower latency in Singapore…',
      placeholder: 'Ask Gemini',
    },
    aliFw: {
      user: 'Route this request to the cheapest Qwen endpoint.',
      assistant: 'Using Qwen via Fireworks — 42% less than the default path.',
      placeholder: 'Message Qwen…',
    },
  },
  authSky: {
    planeBanners: [
      'IC Lueur Ai',
      'Pay as you go',
      'API keys in minutes',
      'One gateway, many models',
      'Track usage in real time',
      'No procurement hoops',
    ],
  },
  signIn: {
    twoStepTitle: 'Two-step verification',
    signInTitle: 'Sign in',
    ledeRecovery: 'Enter one of your saved recovery codes.',
    ledeAuthenticator: 'Enter the code from your authenticator app.',
    ledeSignIn: 'Access your credit balance, API keys and usage.',
    errTooMany: 'Too many attempts. Please wait a moment and try again.',
    errCredentials: 'That email and password did not match.',
    errRecoveryInvalid: 'That recovery code is not valid, or has already been used.',
    errCodeInvalid: 'That code is not valid.',
    recoveryCodeLabel: 'Recovery code',
    authCodeLabel: 'Code from your authenticator app',
    otpCodeLabel: 'Enter the 6-digit code',
    checking: 'Checking…',
    continue: 'Continue',
    useRecoveryInstead: 'Use a recovery code',
    useAuthenticatorInstead: 'Use my authenticator app instead',
    recoveryHelp:
      'A recovery code can be used once. If you have lost both, a team admin can reset your second factor — we cannot do it from an email alone.',
    methodName: (method) => {
      switch (method) {
        case 'totp':
          return 'Authenticator app';
        case 'sms':
          return 'SMS';
        case 'email_otp':
          return 'Email';
        default:
          return method;
      }
    },
    ledeOTPSent: (destinationMasked) => `We sent a code to ${destinationMasked}.`,
    sendingCode: 'Sending code…',
    resendCode: 'Resend code',
    resendSent: 'Sent again.',
    errSendFailed: 'Could not send the code right now. Try again shortly.',
    switchMethod: 'Use a different method',
    chooseMethodLede: 'How would you like to receive your code?',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    signingIn: 'Signing in…',
    signIn: 'Sign in',
    noAccount: "Don't have an account?",
    signUpLink: 'Sign up',
    errEmailNotVerified: 'Please verify your email before signing in — check your inbox for the link, or request a new one.',
    errTwoFactorSetupRequired: 'Your password is correct, but this account requires an authenticator app that has not been set up yet. Contact your organization admin or support to finish setup.',
    welcomeEyebrow: 'Welcome',
    welcomeHeadline: 'Your credit balance, API\u00A0keys\nand usage — all in one place.',
    welcomeBody: 'Sign in to manage your account and see exactly what you have spent.',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password?',
    orDivider: 'or',
    signInWithGoogle: 'Sign in with Google',
    signInWithMicrosoft: 'Sign in with Microsoft',
    oauthError: (code) => {
      switch (code) {
        case 'google_denied':
          return 'Google sign-in was cancelled.';
        case 'google_email_unverified':
          return "Google reports this email isn't verified. Verify it with Google first, or sign in with a password instead.";
        case 'google_email_conflict':
          return 'This email belongs to an account that Google sign-in cannot be used with. Sign in with a password instead.';
        case 'google_not_configured':
          return 'Sign in with Google will be available soon. Please sign in with a password for now.';
        case 'google_unavailable':
          return "Google sign-in isn't available right now. Try again shortly, or sign in with a password instead.";
        case 'microsoft_denied':
          return 'Microsoft sign-in was cancelled.';
        case 'microsoft_email_missing':
          return "Microsoft didn't share a verified email for this account. Sign in with a password instead.";
        case 'microsoft_email_conflict':
          return 'This email belongs to an account that Microsoft sign-in cannot be used with. Sign in with a password instead.';
        case 'microsoft_not_configured':
          return 'Sign in with Microsoft will be available soon. Please sign in with a password for now.';
        case 'microsoft_unavailable':
          return "Microsoft sign-in isn't available right now. Try again shortly, or sign in with a password instead.";
        case 'oauth_expired':
          return 'That sign-in link expired. Please try again.';
        default:
          return 'Sign-in did not complete. Please try again.';
      }
    },
    showPassword: 'Show',
    hidePassword: 'Hide',
  },
  passwordValidation: {
    minLength: 'At least 8 characters',
    special: 'At least 1 special character',
    uppercase: 'At least 1 uppercase letter',
    number: 'At least 1 number',
  },
  signUp: {
    title: 'Create your account',
    lede: 'Get an API key and start paying for what you use.',
    accountTypeLabel: 'Account type',
    accountTypeIndividual: 'Individual',
    accountTypeOrganization: 'Organization',
    emailLabel: 'Email',
    emailLabelOrganization: 'Business email',
    displayNameLabel: 'Name (optional)',
    displayNameLabelOrganization: 'Contact name',
    orgNameLabel: 'Company name',
    taxIdLabel: 'Tax ID',
    taxIdHint: '13-digit Thai taxpayer ID, as registered with the Revenue Department.',
    phoneLabel: 'Phone number',
    countryCodeLabel: 'Country code',
    passwordLabel: 'Password',
    passwordHint: 'At least 8 characters, with uppercase, lowercase, a number, and a special character.',
    confirmPasswordLabel: 'Confirm password',
    creatingAccount: 'Creating account…',
    createAccount: 'Create account',
    errTooMany: 'Too many attempts. Please wait a moment and try again.',
    errWeakPassword:
      'Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character. It cannot be your email address.',
    errInvalidPhone: 'Enter a valid phone number.',
    errPhoneTaken: 'This phone number is already registered.',
    errEmailTaken: 'This email is already registered. Sign in instead, or use a different address.',
    errEmailRequired: 'Enter your email address.',
    errCheckingEmail: 'Checking email…',
    errPhoneRequired: 'Enter your phone number.',
    errPasswordRequired: 'Enter a password.',
    errPasswordMismatch: 'Passwords do not match.',
    errGeneric: 'Something went wrong. Please try again.',
    errTermsNotAccepted: 'You must accept the Terms of Use and Privacy Policy to create an account.',
    errOrgNameRequired: 'Enter your company name.',
    errInvalidTaxId: 'Enter a valid 13-digit Thai taxpayer ID.',
    errContactNameRequired: 'Enter a contact name.',
    promoCodeLabel: 'Promo code (optional)',
    promoCodeHint: "Got a card from an event? Enter its code — the amount is a surprise, you'll see it once your email is verified.",
    errCheckingPromoCode: 'Checking code…',
    errPromoCodeNotFound: 'That code was not recognised. Double-check what is printed on the card.',
    errPromoCodeUsed: 'This code has already been used.',
    errPromoCodeNotYetValid: 'This code is not active yet.',
    errPromoCodeExpired: 'This code has expired.',
    referralSourceLabel: 'How did you hear about us? (optional)',
    referralSourceOptions: {
      aiExpo: 'AI Expo Thailand 2026 (Oct)',
      facebook: 'Facebook',
      instagram: 'Instagram',
      line: 'LINE',
      friend: 'A friend told me',
      googleSearch: 'Google search',
      other: 'Other',
    },
    referralSourceOtherPlaceholder: 'Tell us where',
    agreePrefix: 'I agree to the',
    agreeTerms: 'Terms of Use',
    agreeAnd: 'and',
    agreePrivacy: 'Privacy Policy',
    pendingTitle: 'Check your email',
    pendingBody: (email) => `We sent a verification link to ${email}. Follow it to finish creating your account.`,
    resendButton: 'Resend email',
    resendBusy: 'Sending…',
    resendSent: 'Sent — check your inbox.',
    haveAccount: 'Already have an account?',
    signInLink: 'Sign in',
    showPassword: 'Show',
    hidePassword: 'Hide',
    welcomeEyebrow: 'Get started',
    welcomeHeadline: 'Create an account\nand get an\u00A0API\u00A0key in minutes.',
    welcomeBody: 'Pay only for what you use — no minimum commitment, no procurement call.',
  },
  verifyEmail: {
    verifyingTitle: 'Verifying your email…',
    verifyingBody: 'One moment.',
    successTitle: 'Email verified',
    successBody: 'Your account is ready. Sign in to continue.',
    promoCredited: (amount) => `Your promo code was redeemed — ฿${amount} has been added to your account.`,
    goToSignIn: 'Go to sign in',
    failedTitle: 'This link no longer works',
    failedBody: 'It may have expired or already been used. Request a new one below.',
    resendEmailLabel: 'Email',
    resendButton: 'Send a new link',
    resendBusy: 'Sending…',
    resendSent: 'Sent — check your inbox.',
    missingTokenBody: 'This link is missing its verification token.',
    welcomeEyebrow: 'Almost there',
    welcomeHeadline: 'One click and your account\nis ready to use.',
    welcomeBody: "We're confirming this is really you\nbefore your first sign-in.",
  },
  twoFactorRequired: {
    eyebrow: 'One more step',
    title: 'Set up your authenticator app',
    body: 'Your account manages an organization, so it requires an authenticator app for every sign-in. Set it up now to finish activating your account.',
    signOutInstead: 'Sign out instead',
  },
  forgotPassword: {
    title: 'Forgot password',
    lede: 'Enter the email for your account. If it exists, we will send a reset link.',
    emailLabel: 'Email',
    sendLink: 'Send reset link',
    sending: 'Sending…',
    sentTitle: 'Check your email',
    sentBody: (email) =>
      `If an account exists for ${email}, a reset link is on its way. The link expires in one hour.`,
    backToSignIn: 'Back to sign in',
    errTooMany: 'Too many requests. Please wait a moment and try again.',
    errGeneric: 'Something went wrong. Please try again.',
    welcomeEyebrow: 'Welcome',
    welcomeHeadline: 'Reset access\nto your account.',
    welcomeBody: 'We will email a one-time link\nso you can choose a new password.',
  },
  resetPassword: {
    title: 'Choose a new password',
    lede: 'Pick a strong password, then sign in with it.',
    passwordLabel: 'New password',
    passwordHint: 'At least 8 characters, with uppercase, lowercase, a number, and a special character.',
    confirmPasswordLabel: 'Confirm password',
    savePassword: 'Save password',
    saving: 'Saving…',
    successTitle: 'Password updated',
    successBody: 'Sign in with your new password to continue.',
    goToSignIn: 'Go to sign in',
    failedTitle: 'This link no longer works',
    missingTokenBody: 'This link is missing its reset token.',
    invalidTokenBody:
      'This link may have expired, already been used, or a newer reset link may have been requested since. Request a new one.',
    checkingToken: 'Checking your link…',
    requestNewLink: 'Request a new link',
    errPasswordMismatch: 'Passwords do not match.',
    errWeakPassword:
      'Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.',
    errInvalidToken:
      'This link may have expired, already been used, or a newer reset link may have been requested since.',
    errTooMany: 'Too many attempts. Please wait a moment and try again.',
    errGeneric: 'Something went wrong. Please try again.',
    showPassword: 'Show',
    hidePassword: 'Hide',
    welcomeEyebrow: 'Almost there',
    welcomeHeadline: 'Set a new password\nand you are back in.',
    welcomeBody: 'After saving, sign in with the new password —\nsessions on other devices are signed out.',
  },
  acceptInvite: {
    title: 'Accept your invite',
    lede: "You've been invited to join a team on IC Lueur Ai. Set up your account to accept.",
    ledeExisting: "You've been invited to join a team on IC Lueur Ai. Accept to add this team to your account.",
    displayNameLabel: 'Name (optional)',
    phoneLabel: 'Phone number',
    countryCodeLabel: 'Country code',
    passwordLabel: 'Password',
    passwordHint: 'At least 8 characters, with uppercase, lowercase, a number, and a special character.',
    confirmPasswordLabel: 'Confirm password',
    createAccount: 'Accept and create account',
    creatingAccount: 'Setting up your account…',
    acceptJoin: 'Accept invitation',
    accepting: 'Joining team…',
    showPassword: 'Show',
    hidePassword: 'Hide',
    errPasswordMismatch: 'Passwords do not match.',
    errWeakPassword:
      'Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.',
    errInvalidPhone: 'Enter a valid phone number.',
    errPhoneTaken: 'This phone number is already registered.',
    errAccountExists: 'This email already has an account — sign in instead.',
    errAlreadyMember: 'This account already belongs to the team.',
    errTooMany: 'Too many attempts. Please wait a moment and try again.',
    errGeneric: 'Something went wrong. Please try again.',
    missingTokenTitle: 'This link is missing its invite token',
    missingTokenBody: 'Ask whoever invited you to send the link again.',
    checkingToken: 'Checking your invite…',
    invalidTokenTitle: 'This invite no longer works',
    invalidTokenBody: 'It may have expired or already been used. Ask a team admin to send a new one.',
    goToSignIn: 'Go to sign in',
    welcomeEyebrow: "You're invited",
    welcomeHeadline: 'Join your team\non IC Lueur Ai.',
    welcomeBody: 'One quick step and you can see keys, usage\nand the credit wallet with the rest of your team.',
  },
  topUp: {
    subtitle: 'Add prepaid credit by PromptPay or card.',
    currentBalance: 'Current balance',
    asOf: (date) => `As of ${date}.`,
    balanceRestricted: (isB2B) =>
      isB2B ? 'Only admins and owners can view the org balance.' : 'Only admins and owners can view the account balance.',
    topUpRestrictedTitle: 'Only admins and owners can add credit',
    topUpRestrictedBody: (isB2B) =>
      isB2B ? 'Ask an admin or the owner of your organization to top up.' : 'Ask an admin or the owner of your account to top up.',
    inProgress: 'Top-up in progress',
    backToForm: 'Back to edit the amount',
    qrAlt: 'PromptPay QR code for this top-up',
    status: {
      pending: 'Awaiting payment',
      paid: 'Paid',
      expired: 'Expired',
      failed: 'Failed',
    },
    scanNote:
      'Scan with your banking app. Credit is added automatically once payment is confirmed — this page checks every few seconds, you do not need to refresh.',
    companyQrAlt: 'QR code for a direct transfer to our PromptPay account',
    companyQrNote:
      'Our usual payment provider is unavailable right now, so this QR pays our PromptPay account directly. After you pay, upload your slip below — a member of staff credits it once the bank transfer is confirmed, so this can take a little longer than the usual instant credit.',
    companyQrUploadPrompt: 'Already paid? Upload your slip',
    expires: (date) => `Expires ${date}.`,
    saveQrCode: 'Save QR Code',
    saveQrSaving: 'Saving…',
    saveQrError: 'Could not save the QR code. Try again, or long-press the image to save it.',
    saveQrImageTitle: 'IC Lueur Ai · PromptPay',
    creditAdded: 'Credit has been added to your balance above.',
    expired: 'This top-up expired before payment was received.',
    failed: 'This top-up could not be completed.',
    startAnother: 'Start a new top-up',
    addCredit: 'Add credit',
    custom: 'Custom',
    stepAmountLabel: 'Amount',
    amountLabel: 'Amount (THB)',
    amountPlaceholder: 'e.g. 750.00',
    vatInclusiveNote: (amount) => `Total with 7% VAT: ฿${amount}`,
    vatSummaryBeforeConfirm: (amount) => `You will be charged ฿${amount}, including 7% VAT.`,
    paymentMethodLabel: 'Payment method',
    methodPromptPay: 'PromptPay',
    methodCard: 'Credit / debit card',
    // Unused in the English UI (see the field's own doc comment in types.ts) — kept only so every locale satisfies the Dictionary type.
    methodPromptPaySubtitle: '(QR Code)',
    methodCardSubtitle: '(Credit Card)',
    paidByPromptPay: 'Paid by PromptPay — a QR code appears after you continue.',
    paidByPromptPayExpiry: (minutes) =>
      `The QR is valid for ${minutes} minutes — if it expires before you pay, the system cancels the top-up automatically and history below shows “Expired” (nothing is charged). To try again, start a new top-up for a fresh QR.`,
    paidByCard: 'Paid by card — your bank may ask you to verify the payment.',
    afterContinueBadge: 'Next',
    afterContinueTitle: 'After you continue',
    afterContinueSteps: [
      'The system shows a QR code to scan in your banking app',
      'Scan and transfer the exact amount shown',
      'Credit is added automatically when payment succeeds (no extra confirm, no refresh)',
      'History below changes from “Awaiting payment” to “Paid”',
    ],
    payHowTitle: 'How to pay with PromptPay',
    payHowSteps: [
      'Open your banking app (or any app that supports PromptPay)',
      'Scan this QR (or tap “Save QR” and scan from your gallery)',
      'Check the amount matches your top-up, then confirm the transfer',
      'Come back here — status updates automatically; no need to refresh the screen',
    ],
    payHowSuccessNote: 'When payment succeeds, credit is added to the balance above automatically.',
    timeRemaining: (mmss) => `Time left ${mmss}`,
    waitingForPayment: 'Waiting for payment…',
    topUpSuccessToast: 'Top-up successful — credit has been added.',
    expiredGuidance:
      'This top-up has expired. If you have not paid yet, start a new top-up for a fresh QR. If you already paid but the top-up expired before we saw the payment, use “Already paid? Upload slip” below.',
    cardNameLabel: 'Name on card',
    cardNamePlaceholder: 'As printed on the card',
    cardNumberLabel: 'Card number',
    cardNumberPlaceholder: '4242 4242 4242 4242',
    cardExpiryLabel: 'Expiry',
    cardCVCLabel: 'CVC',
    cardCVCPlaceholder: '123',
    starting: 'Starting…',
    continueToPay: (amount) => `Continue to pay ฿${amount}`,
    enterAmount: 'Enter an amount.',
    amountMustBePositive: 'Amount must be greater than zero.',
    belowMinimum: (min) => `The minimum top-up is ${min} THB.`,
    notValidTHB: 'Not a valid THB amount.',
    notValidAmount: 'Not a valid amount.',
    historyHeading: 'Top-up history',
    historyNote:
      'Every top-up on this account, newest first. Credit is added only when we receive confirmed payment, so a top-up can sit as pending for a while before it becomes paid.',
    historyEmptyTitle: 'No top-ups in this range',
    historyEmptyBody: 'Try widening the date range.',
    historyLoadError: 'Could not load top-up history. Try again.',
    colStarted: 'Started',
    colAmount: 'Amount',
    colAmountVatNote: (vatAmount) => `VAT 7%: ฿${vatAmount}`,
    colStatus: 'Status',
    colInvoice: 'Receipt / tax invoice',
    invoiceButton: 'Download',
    invoiceGenerating: 'Generating…',
    invoiceComingSoon: 'Still generating — try again in a moment.',
    invoiceDownloadError: 'Could not download the invoice. Try again.',
    taxInvoiceHeading: 'Tax invoice details',
    taxInvoiceFullLabel: 'Request a full tax invoice',
    useAccountInfoLabel: "Use my account's name and email",
    useTaxProfileLabel: 'Use latest tax invoice info',
    buyerNameLabel: 'Name',
    buyerNamePlaceholder: 'Full name',
    buyerCompanyNameLabel: 'Company name',
    buyerCompanyNamePlaceholder: 'Full registered company name',
    buyerEmailLabel: 'Email',
    buyerEmailPlaceholder: 'you@example.com',
    buyerTypeLabel: 'Buyer type',
    buyerTypeIndividual: 'Individual',
    buyerTypeJuristic: 'Company / juristic person',
    juristicWhtNote:
      'Individual accounts support tax invoices issued to a company name for expense tracking only. Withholding tax (3%) deduction is not supported. To claim WHT, switch to a B2B organisation account.',
    taxIdLabelIndividual: 'National ID number',
    taxIdPlaceholderIndividual: '13-digit national ID card number',
    taxIdLabelJuristic: 'Tax ID',
    taxIdPlaceholderJuristic: '13-digit taxpayer ID',
    taxIdInvalid: 'Not a valid 13-digit number.',
    branchCodeLabel: 'Branch code',
    branchCodePlaceholder: '00000 for head office',
    branchCodeInvalid: 'Branch code must be 5 digits ("00000" for head office)',
    branchNameLabel: 'Branch name',
    branchNamePlaceholder: 'e.g. Silom branch',
    addressHouseNoLabel: 'Address',
    addressHouseNoPlaceholder: 'House / building number, street',
    addressProvinceLabel: 'Province',
    addressProvincePlaceholder: 'Select province',
    addressDistrictLabel: 'District',
    addressDistrictPlaceholder: 'Select district',
    addressDistrictSelectProvinceFirst: 'Select a province first',
    addressSubdistrictLabel: 'Subdistrict',
    addressSubdistrictPlaceholder: 'Select subdistrict',
    addressSubdistrictSelectDistrictFirst: 'Select a district first',
    addressPostalCodeLabel: 'Postal code',
    addressPostalCodePlaceholder: 'Filled in automatically once subdistrict is selected',
    addressSelectNoResults: 'No matches',
    addressSelectTypeHint: 'You can type to search',
    taxInvoiceDraftNote:
      'This generates a draft receipt for your records. Automated e-Tax invoicing is not live yet.',
    createError: 'Could not start a top-up.',
    preparingPayment: 'Preparing your PromptPay QR…',
    preparingCardPayment: 'Preparing your card payment…',
    awaitingCardConfirmation: 'Confirming your payment — this can take a few seconds.',
    paymentUnavailable:
      'Could not reach the payment provider. Check your connection and try again — no payment has been taken.',
    paymentsNotConfigured: 'Payments are not available in this environment. No payment has been taken.',
    sourceError: 'The payment provider could not prepare this payment. No payment has been taken.',
    paymentExpired: 'This payment expired before it could be completed. Start a new top-up to get a fresh QR.',
    uploadSlipButton: 'Already paid? Upload slip',
    uploadSlipModalTitle: 'Upload your payment slip',
    uploadSlipInstructions:
      "If you already transferred the amount shown on the QR screen (which includes 7% VAT) for this top-up, upload a photo of the bank slip. We'll check it against your bank's own record and credit your account once a member of staff has reviewed it — this is not instant.",
    uploadSlipChooseFile: 'Choose photo',
    uploadSlipChooseAnother: 'Choose a different photo',
    uploadSlipQrFound: 'QR code found on the slip.',
    uploadSlipQrNotFound:
      "We couldn't read a QR code on this photo. You can still upload it — a member of staff will check it by hand.",
    uploadSlipSubmit: 'Submit slip',
    uploadSlipSubmitting: 'Submitting…',
    uploadSlipSuccess:
      "Slip received. We'll credit your account once a member of staff has checked it against your bank's record — this can take a while, not instant.",
    uploadSlipNotImage: 'Please choose an image file.',
    uploadSlipTooLarge: 'This photo is too large (max 6 MB).',
    uploadSlipError: 'Could not submit the slip. Check your connection and try again.',
    colAction: 'Upload payment slip',
  },
  whtClaim: {
    colSelect: 'Select to submit',
    colSubmitDoc: 'Submit 50 Tawi (one at a time)',
    selectAllLabel: 'Select all',
    claimButton: 'Claim withholding tax',
    claimSelectedButton: (n) => `Claim withholding tax (${n} selected)`,
    modalTitle: 'Claim withholding-tax credit',
    modalIntro:
      'One 50 Tawi certificate can cover a single top-up or several at once (e.g. one covering a whole month) — pick every top-up this certificate covers below.',
    itemsSelectedLabel: (n) => `Top-ups covered by this certificate (${n} selected)`,
    expectedWHTLabel: (amount) => `3% = ฿${amount}`,
    computedTotalLabel: (amount) => `Computed total: ฿${amount}`,
    docNumberLabel: 'Certificate number',
    docNumberPlaceholder: 'As printed on the certificate',
    docDateLabel: 'Certificate date',
    declaredAmountLabel: 'Declared withholding amount (THB)',
    declaredAmountPlaceholder: 'As shown on the certificate',
    declaredTaxIdLabel: 'Taxpayer ID on the certificate',
    declaredTaxIdPlaceholder: '13-digit tax ID',
    declaredTaxIdInvalid: 'This does not look like a valid 13-digit tax ID.',
    fileLabel: 'Certificate file',
    chooseFile: 'Choose file',
    chooseAnotherFile: 'Choose another file',
    fileTooLarge: 'This file is too large (max 10 MB).',
    fileUnsupportedType: 'Upload a JPEG/PNG/WebP/GIF image or a PDF.',
    fileError: 'Could not read this file. Try again.',
    submitButton: 'Submit claim',
    submitting: 'Submitting…',
    submitError: 'Could not submit this claim. Check your connection and try again.',
    successTitle: 'Claim submitted',
    successBody: (amount) =>
      `Submitted for staff review. If approved, ฿${amount} will be credited to your wallet.`,
    warningsIntro: 'Staff will take a closer look at this claim because:',
    warningLabels: {
      org_tax_id_not_on_file: "your organization has no tax ID on file to check the certificate against yet",
      declared_amount_mismatch: 'the declared amount differs from the computed amount by more than the usual rounding allowance',
      doc_date_before_topup: 'the certificate date is before the top-up it covers',
      doc_date_too_far_after_topup: 'the certificate date is more than 90 days after the top-up it covers',
    },
    eligibleLoadError: 'Could not load top-ups eligible for a withholding-tax claim.',
    historyHeading: 'Withholding-tax claims',
    historyLede: 'Every certificate you have submitted, and what happened to it.',
    historyEmpty: 'No withholding-tax claims yet.',
    historyLoadError: 'Could not load your claim history.',
    colDocNumber: 'Certificate no.',
    colDocDate: 'Certificate date',
    colDeclaredAmount: 'Declared amount',
    colComputedAmount: 'Computed amount',
    colStatus: 'Status',
    colReason: 'Reason',
    statusLabels: {
      pending_review: 'pending review',
      approved: 'approved',
      rejected: 'rejected',
    },
  },
  promoCode: {
    title: 'Have a promo card?',
    description: "Enter the code printed on your card. The amount is a surprise — you'll see it once it's added.",
    placeholder: 'Enter code',
    submit: 'Redeem',
    submitting: 'Redeeming…',
    successToast: (amount) => `You got ฿${amount} — added to your balance.`,
    errorNotFound: 'That code was not recognised. Double-check what is printed on the card.',
    errorUsed: 'This code has already been used.',
    errorExpired: 'This code has expired.',
    errorCapHit: "Today's giveaway budget is spent — this code still works tomorrow.",
    errorGeneric: 'Could not redeem this code. Please try again.',
  },
  quota: {
    title: 'Auto-fallback to paid billing',
    description:
      'When a free promotional model runs out of its weekly quota, choose what happens next: keep going by paying from your Wallet as usual, or pause access to that model until it resets the following Monday.',
    enabledLabel: 'Auto-pay from Wallet when the free quota runs out',
    disabledLabel: 'Block access until the weekly quota resets',
    saving: 'Saving…',
    restricted: () => 'Only admins and owners can change this setting.',
    saveError: 'Could not save this setting.',
    loadError: 'Could not load this setting.',
  },
  firstTopUpPrompt: {
    title: 'Nice — your wallet is funded!',
    body: 'If a free promotional model runs out of its weekly quota, would you like requests to keep going automatically, billed from this wallet? You can change this anytime on the Credit Wallet page.',
    accept: 'Yes, auto-pay when it runs out',
    decline: 'Not now — keep the free quota only',
  },
  orgSwitcher: {
    label: 'Account',
    optionLabel: (org, role) => `${org} (${role})`,
    switchError: 'Could not switch. Try again.',
  },
  orgChooser: {
    title: 'Choose an account',
    body: 'Your login belongs to more than one account. Pick which one to work in — you can switch anytime from the top bar.',
    defaultBadge: 'Default',
  },
  promoCreditWelcome: {
    title: 'You got lucky!',
    body: (amount) => `Your promo code was redeemed — ฿${amount} has been added to your credit balance.`,
    close: 'Nice, thanks',
  },
  playground: {
    subtitle: 'Send a real request with a real key and see exactly what comes back.',
    unavailableTitle: 'Not available here',
    unavailableBody: "The playground only runs in dev and local — it isn't part of the production build.",
    gatewayUrlLabel: 'Gateway URL',
    apiKeyLabel: 'API key',
    apiKeyPlaceholder: 'llmr_sk_…',
    showKey: 'Show',
    hideKey: 'Hide',
    modelLabel: 'Model alias',
    modelPlaceholder: 'Select a model',
    modelSearchHint: 'Type to search',
    modelNoResults: 'No models match your search',
    testConnection: 'Test connection',
    testing: 'Testing…',
    statusNotTested: 'Not tested',
    statusConnected: 'Connected',
    statusUnreachable: 'Unreachable',
    emptyTitle: 'Nothing sent yet',
    emptyBodyReady: 'Pick a model alias and send a message below. Every reply shows the alias it actually routed to.',
    emptyBodyWithFree: 'Your wallet has no credit yet — pick a model with the Free badge to try without topping up, or top up to use paid models.',
    emptyBodyNeedCredit: 'Your wallet has no credit and there is no free model to try this week. {topUpLink} to start testing.',
    emptyBodyNeedCreditMember: (isB2B) =>
      isB2B
        ? 'Your organization has no credit and there is no free model to try this week. Ask an admin or the owner to top up.'
        : 'Your account has no credit and there is no free model to try this week. Ask an admin or the owner to top up.',
    messagePlaceholder: 'Type a message… (Enter to send, Shift+Enter for a new line)',
    send: 'Send message',
    clearConversation: 'Clear conversation',
    messagesSentCount: (n) => `Messages: ${n}`,
    viewRawResponse: 'View raw response',
    hideRawResponse: 'Hide raw response',
    needKeyAlert: 'Paste an API key first.',
    needModelAlert: 'Enter a model alias first, e.g. gpt-5-mini.',
    needAlertTitle: 'Before you send',
    networkErrorBody: (url) => `Could not reach ${url} — is the gateway running, and is it reachable from this browser?`,
    costNote: 'The actual cost is not included in this response. Please check the {usageLink}.',
    costNoteUsageLink: 'Usage page',
    apiKeyOptionalHint: 'Leave blank to use your signed-in account, or paste an API key to test a different one.',
    sessionError: "Couldn't load your account credit — paste an API key to continue.",
    configHeading: 'Connection',
    chatHeading: 'Conversation',
    nudgeAdminCta: () => 'Notify an admin or the owner',
    nudgeAdminTitle: () => 'Notify an admin or the owner',
    freeBadge: 'Free',
    creditBannerWithFree:
      'No credit in the wallet yet, so paid models will not run. Pick a model with the Free badge to try now, or top up to use the rest of the catalogue.',
    creditBannerWithFreeSpent:
      'Your credit balance is used up, so paid models will not run. Pick a model with the Free badge to try now, or top up credit to use the rest of the catalogue.',
    creditBannerNoFree: 'No credit in the wallet, and there is no free model to try this week. Top up to start testing.',
    creditBannerNoFreeSpent:
      'Your credit balance is used up, and there is no free model to try this week. Top up credit to continue testing.',
    goTopUpCta: 'Top up credit',
    creditErrorBody: 'Not enough credit for this test. Please top up first.',
    creditErrorFreeHint: 'Or pick a model with the Free badge to try without topping up.',
  },
  team: {
    subtitle: (isB2B) => (isB2B ? 'Who can sign in to this organization, and what they can do.' : 'Who can sign in to your account, and what they can do.'),
    loadError: 'Could not load the team.',
    limitsOwnerAdmin: (used, max) => `Admins ${used} of ${max}`,
    limitsOwnerAdminHint: 'Admins include the team owner and organization admins',
    limitsMember: (used, max) => `Members ${used} of ${max}`,
    limitsMemberUnlimited: (used) => `Members: ${used} (no limit)`,
    inviteHeading: 'Add a member',
    emailLabel: 'Email',
    emailPlaceholder: 'colleague@company.com',
    roleLabel: 'Role',
    role: {
      owner: 'Owner',
      admin: 'Admin',
      member: 'Member',
    },
    expiresInLabel: 'Expires in',
    expiresInOptions: {
      '1': '1 day',
      '3': '3 days',
      '7': '7 days',
      '30': '30 days',
      '365': '1 year',
    },
    inviting: 'Sending invitation…',
    inviteButton: 'Send invitation',
    inviteError: 'Could not send this invitation.',
    inviteSent: (email) => `Invitation sent to ${email}`,
    inviteErrorAlreadyMember: (isB2B) =>
      isB2B ? 'This account already belongs to the organization.' : 'This account is already on your team.',
    inviteErrorAlreadyPending: 'An invite is already pending for this email. To send a new one, use "Resend" in the pending invites table below.',
    rosterHeading: 'Members',
    colName: 'Name',
    colEmail: 'Email',
    colRole: 'Role',
    colJoined: 'Joined',
    actionsAria: 'Actions',
    you: 'you',
    remove: 'Remove',
    removeTitle: 'Remove this member?',
    removeBody: (name, isB2B) =>
      isB2B
        ? `${name} will immediately lose access to this organization — its keys, usage and credit wallet.`
        : `${name} will immediately lose access to your team — its keys, usage and credit wallet.`,
    removeConfirmLabel: 'Remove member',
    removeError: 'Could not remove this member.',
    emptyTitle: 'Just you, for now',
    emptyBody: (isB2B) =>
      isB2B ? 'Add a colleague above to give them access to this organization.' : 'Add a colleague above to give them access to your team.',
    pendingInvitesHeading: 'Pending invites',
    pendingInvitesEmpty: 'No pending invites.',
    colExpires: 'Expires',
    resend: 'Resend',
    resending: 'Resending…',
    resendError: 'Could not resend this invite.',
    cancelInvite: 'Cancel',
    cancelInviteTitle: 'Cancel this invite?',
    cancelInviteBody: (email) => `${email} will no longer be able to use this invite link to join.`,
    cancelInviteConfirmLabel: 'Cancel invite',
    cancelInviteError: 'Could not cancel this invite.',
    invitesLoadError: 'Could not load pending invites.',
    editInvite: 'Edit',
    editInviteTitle: (email) => `Edit invite for ${email}`,
    editInviteSave: 'Save changes',
    editInviteError: 'Could not update this invite.',
    editKeepExpiry: "Don't change",
    historyHeading: 'Invitation history',
    historySubtitle: 'Every invite that has already concluded — expired, cancelled, or accepted.',
    historyEmpty: 'No invitation history yet.',
    historyLoadError: 'Could not load invitation history.',
    colInvitedBy: 'Invited by',
    colStatus: 'Status',
    colOccurredAt: 'Date',
    historyStatus: {
      accepted: 'Accepted',
      revoked: 'Cancelled',
      expired: 'Expired',
    },
    historyInvitedByUnknown: 'Deleted account',
    budgets: {
      heading: 'Spend limits',
      subtitle: 'Set a daily or monthly THB cap for the organization, or override it for one member.',
      warning:
        "These limits attribute spend to a key's creator, not to who actually used it. A key shared between teammates makes a limit inaccurate and easy to work around — for an accurate limit, give each person their own key.",
      loadError: 'Could not load spend limits.',
      periodLabel: { daily: 'Daily default', monthly: 'Monthly default' },
      periodName: { daily: 'daily', monthly: 'monthly' },
      notSet: 'Not set',
      setButton: 'Set',
      editButton: 'Edit',
      removeButton: 'Remove',
      removeDefaultTitle: 'Remove this default?',
      removeDefaultBody: (period) =>
        `Members with their own override are unaffected. Members with neither fall back to unlimited for the ${period} period.`,
      removeOverrideTitle: 'Remove this override?',
      removeOverrideBody: (name, period) =>
        `${name} will fall back to the organization's ${period} default, or unlimited if there is none.`,
      removeConfirmLabel: 'Remove',
      removeError: 'Could not remove this limit.',
      overridesHeading: 'Per-member overrides',
      overridesEmpty: 'No member has a limit of their own — everyone uses the organization defaults above.',
      addOverrideButton: 'Set a limit for a member',
      colMember: 'Member',
      colPeriod: 'Period',
      colLimit: 'Limit',
      actionsAria: 'Actions',
      memberLabel: 'Member',
      memberPlaceholder: 'Choose a member',
      periodSelectLabel: 'Period',
      amountLabel: 'Limit (THB)',
      amountPlaceholder: 'e.g. 500.00',
      modalSetDefaultTitle: (period) => `Set the ${period} default`,
      modalEditOverrideTitle: (name) => `${name}'s limit`,
      modalAddOverrideTitle: 'Set a limit for a member',
      save: 'Save',
      saving: 'Saving…',
      saveError: 'Could not save this limit.',
    },
  },
  reports: {
    subtitle: 'Create a report on screen, or export it as Excel or PDF.',
    reportLabel: 'Report',
    fromLabel: 'Start date',
    toLabel: 'End date',
    run: 'Create report',
    running: 'Creating report…',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    exporting: 'Generating…',
    emptyTitle: 'Nothing to report',
    emptyBody: 'No activity in this period. Try a wider date range.',
    loadError: 'Could not create the report. Please try again.',
    exportError: 'Could not generate the file.',
    renderUnavailable: 'PDF rendering is temporarily unavailable. Try again in a moment, or export as Excel.',
    downloadReady: (name) => `${name} is downloading.`,
    download: 'Download again',
    rowCount: (n) => `${n.toLocaleString()} row${n === 1 ? '' : 's'}`,
    generatedAt: (when) => `Generated ${when}`,
    linkExpires: (when) => `Link expires ${when}`,
    noReports: 'No reports are available for your account.',
    summaryAria: 'Summary totals for the selected period',
    summaryRequests: 'All requests',
    summaryInputTokens: 'Input tokens',
    summaryOutputTokens: 'Output tokens',
    summaryTotalTokens: 'Total tokens',
    summaryTotalCost: 'Total spend',
    ledgerKind: {
      topup: 'Top-up',
      usage: 'Usage',
      adjustment: 'Adjustment',
      refund: 'Refund',
      reversal: 'Reversal',
    },
    usageStatus: {
      completed: 'Completed',
      interrupted: 'Interrupted',
      failed: 'Failed',
    },
    invoiceStatus: {
      draft: 'Draft',
      issued: 'Issued',
      void: 'Void',
    },
    invoiceDocType: {
      abbreviated: 'Abbreviated',
      full: 'Full',
    },
    reconciliationStatus: {
      ok: 'OK',
      drift_detected: 'Drift detected',
      failed: 'Failed',
    },
  },

  usage: {
    subtitle: 'Spend over time, broken down by day, model or key.',
    loadErrorSummary: 'Could not load the usage summary.',
    fromLabel: 'Start date',
    toLabel: 'End date',
    groupByLabel: 'Group as',
    groupDay: 'Daily',
    groupModel: 'By model',
    groupKey: 'By API key',
    /** Noun form for table column headers and the “Breakdown by …” heading. */
    groupAxisDay: 'Day',
    groupAxisModel: 'Model',
    groupAxisKey: 'API key',
    totalSpend: 'Total spend',
    rangeNote: (from, to, group) => `${from} to ${to}, grouped as ${group.toLowerCase()}.`,
    breakdownBy: (group) => `Breakdown by ${group.toLowerCase()}`,
    emptyTitle: 'No usage in this range',
    emptyBody: 'Try widening the date range.',
    showingTop: (n, total) => `Showing the top ${n} of ${total} — every row is in the table below.`,
    keyNameNone: 'Unnamed',
    colRequests: 'Requests',
    colSuccess: 'Success',
    colFailed: 'Failed',
    colInputTok: 'Input tok',
    colCacheReadTok: 'Cache read tok',
    colOutputTok: 'Output tok',
    colTotalTok: 'Total tok*',
    colCost: 'Cost (THB)',
    quotaFootnote: '* Total = input + cache read + output — matches what counts toward the weekly free quota',
    freeBadge: 'free',
    freeBadgeTitle: 'Free promotional model — you were not charged for this request',
    savedCost: (amount) => `saved ฿${amount}`,
    allRequests: 'All requests',
    loadErrorEvents: 'Could not load usage events.',
    colWhen: 'When',
    colModel: 'Model',
    colProvider: 'Provider',
    colRegion: 'Region',
    colStatus: 'Status',
    colIn: 'In',
    colOut: 'Out',
    emptyRange: 'No requests in this range',
    statusFilterLabel: 'Status',
    statusAll: 'All',
    statusSuccess: 'Success',
    statusFailed: 'Failed',
    statusInterrupted: 'Interrupted',
    requesterLabel: 'User',
    requesterAll: 'All users',
    requesterSearchHint: 'Search by name or email',
    requesterNoResults: 'No member matches your search',
    requesterNone: 'Unknown',
    colRequester: 'Request sender',
    requesterModelsTitle: (name) => `Models ${name} used`,
    requesterBadge: (name) => name,
    quotaCardTitle: 'Weekly free-model quota',
    quotaResetsAt: (date: string) => `Resets ${date}`,
    quotaTokensLabel: 'Tokens',
    quotaRequestsLabel: 'Requests',
    quotaExceeded: 'Quota exceeded',
  },
  onboarding: {
    dashboardArea: 'Your dashboard',
    keysArea: 'API keys',
    usageArea: 'Usage',
    modelsArea: 'The model catalogue',
    topupArea: 'Top-ups',
    teamArea: 'Team',
    title: (area) => `${area} becomes available once your account is set up`,
    body: 'The credit wallet, API keys and usage are all organized under your account. If you signed up moments ago, this can take a minute; if an admin is meant to add you to an existing team, check back once they have.',
    note: 'If this looks wrong, contact support with your account email.',
  },
  profile: {
    title: 'Profile settings',
    subtitle: 'Your details and password for this account.',
    changePhoto: 'Change photo',
    uploadPhoto: 'Upload picture',
    deletePhoto: 'Delete picture',
    personalHeading: 'Personal',
    personalDescription: 'Name and username appear in the portal. Email cannot be changed.',
    nameLabel: 'Name',
    usernameLabel: 'Username',
    usernamePlaceholder: 'Choose a username',
    usernameHint: 'You can set this once. After saving it cannot be changed.',
    usernameLockedHint: 'Username is set and cannot be changed.',
    emailLabel: 'Email',
    emailHint: 'Email cannot be changed.',
    dobLabel: 'Date of birth',
    dobDayLabel: 'Day',
    dobMonthLabel: 'Month',
    dobYearLabel: 'Year',
    phoneLabel: 'Phone',
    phoneCountryLabel: 'Country code',
    unlockField: 'Edit',
    linkedProvidersLabel: 'Connected via',
    linkedProviderBadge: (provider: string) =>
      provider === 'google' ? 'Google'
      : provider === 'microsoft' ? 'Microsoft'
      : provider,
    socialOnlyPasswordNote:
      'Your account signs in through Social login and has no password set.',
    securityHeading: 'Password',
    passwordDescription:
      'Enter your current password, then choose a new one (at least 8 characters, with uppercase, lowercase, a number, and a special character).',
    changePassword: 'Change password',
    passwordLockedHint: 'Your password stays hidden until you choose to change it.',
    currentPasswordLabel: 'Current password',
    newPasswordLabel: 'New password',
    confirmPasswordLabel: 'Confirm new password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    save: 'Save',
    saved: 'Profile saved',
    passwordSaved: 'Password updated',
    phoneVerifyBadgeVerified: 'Verified',
    phoneVerifyBadgeUnverified: 'Not verified',
    phoneVerifyStartButton: 'Send verification code',
    phoneVerifySending: 'Sending…',
    phoneVerifyWhyNote: 'Verify your number to use SMS as a second step when you sign in.',
    phoneVerifyModalTitle: 'Verify your phone number',
    phoneVerifySentTo: 'We sent a 6-digit code to {phone}.',
    phoneVerifyCodeLabel: 'Enter the 6-digit code',
    phoneVerifyExpiresIn: 'Code expires in {time}',
    phoneVerifyExpired: 'That code has expired. Close this and send a new one.',
    phoneVerifyConfirmButton: 'Verify',
    phoneVerifyConfirming: 'Verifying…',
    phoneVerifyDone: 'Phone number verified.',
    errPhoneVerifyNoPhone: 'Add and save a phone number first, then verify it.',
    errPhoneVerifyAlreadyVerified: 'This number is already verified.',
    errPhoneVerifyNotMobile: 'This needs to be a mobile number that can receive SMS.',
    errPhoneVerifyCode: "That code didn't match, or it has expired. Try again or send a new one.",
    errPhoneVerifyThrottled: 'Too many requests. Wait a few minutes and try again.',
    errPhoneVerifyUnavailable: 'Codes cannot be sent right now. Try again shortly.',
    errPhoneVerifyGeneric: 'Could not verify the number. Try again.',
    twoFactorHeading: 'Two-factor authentication',
    twoFactorDescription: 'Add a second step when you sign in.',
    twoFactorEnforcedNote: 'Your organization requires this.',
    methodName: (method) => {
      switch (method) {
        case 'totp':
          return 'Authenticator app';
        case 'sms':
          return 'SMS';
        case 'email_otp':
          return 'Email';
        default:
          return method;
      }
    },
    twoFactorEnrolledStatusFor: (method) => `${method} is set up.`,
    twoFactorNotEnrolledStatusFor: (method) => `${method} is not set up yet.`,
    twoFactorSetupButtonFor: (method) => `Set up ${method}`,
    twoFactorSmsConfirmTitle: 'Set up SMS',
    twoFactorSmsConfirmBody: (phone) =>
      `We'll send a one-time code to ${phone}. Confirm to continue.`,
    twoFactorModalTitleFor: (method) => `Set up ${method}`,
    twoFactorScanLede: 'Scan this QR code with your authenticator app, or enter the code below manually.',
    twoFactorSecretLabel: 'Manual entry code',
    twoFactorCodeLabel: 'Enter the 6-digit code from the app',
    twoFactorOTPLede: (destinationMasked) => `We sent a code to ${destinationMasked}.`,
    twoFactorOTPCodeLabel: 'Enter the 6-digit code',
    twoFactorSendingCode: 'Sending code…',
    twoFactorResendCode: 'Resend code',
    twoFactorResendSent: 'Sent again.',
    errTwoFactorSendFailed: 'Could not send the code right now. Try again shortly.',
    twoFactorConfirmButton: 'Confirm',
    twoFactorConfirming: 'Confirming…',
    twoFactorRecoveryModalTitle: 'Save your recovery codes',
    twoFactorRecoveryWarning: 'These codes are shown exactly once.',
    twoFactorRecoveryWarningBody:
      'If you lose your authenticator app, a recovery code is the only way back in — save these somewhere safe before you continue.',
    twoFactorRecoveryCopyAll: 'Copy all',
    twoFactorRecoveryCopied: 'Copied',
    twoFactorRecoveryAckLabel: "I've saved these recovery codes somewhere safe.",
    twoFactorRecoveryDone: 'Done',
    twoFactorDisableButtonFor: (method) => `Remove ${method}`,
    twoFactorDisableModalTitleFor: (method) => `Remove ${method}`,
    twoFactorDisableWarning: 'You need to re-verify before this can be removed.',
    twoFactorDisableTotpLede: 'Enter the current 6-digit code from your authenticator app to confirm.',
    twoFactorDisableOtpLede: (destinationMasked) => `We sent a verification code to ${destinationMasked}.`,
    twoFactorDisableCodeLabel: 'Enter the verification code',
    twoFactorDisableConfirmButton: 'Confirm removal',
    twoFactorDisableConfirming: 'Removing…',
    twoFactorDisableDone: 'Removed.',
    errTwoFactorDisableCode: 'That code was not correct. Try again.',
    errTwoFactorDisableSendFailed: 'Could not send the code right now. Try again shortly.',
    errTwoFactorDisableGeneric: 'Could not remove this right now.',
    securityHistoryHeading: 'Security history',
    securityHistoryDescription: 'Recent security events on your account.',
    securityHistoryFilterLabel: 'Filter by',
    securityHistoryFilterAll: 'All',
    securityHistoryEmpty: 'No events yet.',
    securityHistoryCategoryTitle: (category) => {
      switch (category) {
        case 'two_factor':
          return 'Two-factor authentication';
        case 'password':
          return 'Password';
        case 'recovery_code':
          return 'Recovery codes';
        case 'session':
          return 'Sign-ins';
        default:
          return category;
      }
    },
    securityHistoryEventLabel: (action) => {
      switch (action) {
        case 'auth.totp.enrolled':
          return 'Set up authenticator app';
        case 'auth.sms.enrolled':
          return 'Set up SMS';
        case 'auth.email_otp.enrolled':
          return 'Set up email verification';
        case 'auth.totp.disabled':
          return 'Removed authenticator app';
        case 'auth.sms.disabled':
          return 'Removed SMS';
        case 'auth.email_otp.disabled':
          return 'Removed email verification';
        case 'auth.password_change.completed':
          return 'Changed password';
        case 'auth.recovery_code.used':
          return 'Used a recovery code';
        case 'auth.session.created':
          return 'Signed in';
        default:
          return action;
      }
    },
    errName: 'Enter your name.',
    errUsername: 'Use 3–32 letters, numbers, dots, underscores or hyphens.',
    errUsernameTaken: 'That username is already taken.',
    errUsernameImmutable: 'Username can only be set once.',
    errDob: 'Enter a valid date of birth.',
    errPhone: 'Enter a valid phone number, or leave it blank.',
    errPhoneTaken: 'This phone number is already registered.',
    errAvatarType: 'Choose an image file (JPG or PNG).',
    errAvatarSize: 'Image must be 512 KB or smaller.',
    errCurrentPassword: 'Enter your current password.',
    errNewPassword:
      'New password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.',
    errPasswordSame: 'New password must be different from the current one.',
    errPasswordMismatch: 'New passwords do not match.',
    errWrongPassword: 'Current password is incorrect.',
    errNoPassword: 'This account has no password to change.',
    errPasswordRateLimit: 'Too many attempts. Wait a moment and try again.',
    errPasswordGeneric: 'Could not update the password. Try again.',
    errProfileGeneric: 'Could not save your profile. Try again.',
    errTwoFactorCode: "That code didn't match. Try again.",
    errTwoFactorAlreadyEnrolled: 'Authenticator app is already set up. Use a recovery code or ask an org admin to reset it.',
    errTwoFactorGeneric: 'Could not set up the authenticator app. Try again.',
  },

  guide: {
    eyebrow: 'Help center',
    title: 'User guide',
    intro:
      "Every menu in the system, explained in one place — each section covers what the page is, what it does, and how to get started. Pick a topic on the right to jump straight to it.",
    hint: 'Sections follow the same order as your left-hand menu.',
    onThisPage: 'On this page',
    fieldDoes: 'What it does',
    fieldStart: 'Getting started',
    keysConnectDocsLabel: 'Full connector setup guide →',
    tagAdminOnly: (isB2B) => (isB2B ? 'Admin / org owner only' : 'Admin / owner only'),
    tagEnvGated: 'Shown in some environments only',
    tagViaAvatarMenu: 'Opens from your avatar in the top right',
    tagViaBell: 'Opens from the bell icon in the top right',
    sections: {
      dashboard: {
        lede: 'The first thing you see after signing in — every account status in one view.',
        does: 'Shows your remaining credit balance, a recent-usage heatmap, your latest usage events, and a low-balance warning.',
        start:
          'Check your balance and recent activity here before heading elsewhere — a top-up button appears automatically once your balance runs low.',
      },
      keys: {
        lede: 'Where you manage the API keys that connect your applications to the system.',
        does: 'Create, edit, or revoke keys, and set an expiration date. Routing policy per key is set by your account team. If the system detects unusual activity on a key it suspends it automatically and sends you an in-app notification with a one-click reactivate option.',
        start:
          'Click “Create key”, give it a memorable name and an expiration, then copy it into your code — it is shown in full only once.',
      },
      usage: {
        lede: 'A detailed breakdown of usage, for checking who called what and how much.',
        does: 'View data by date range, by model or key, the success/failure split, and a paginated event log.',
        start: 'Pick a date range and filters to find your most-called model or key, or trace why a request failed.',
      },
      reports: {
        lede: 'Where you summarize and download data to use outside the system.',
        does: 'Export usage and cost reports as a file (e.g. CSV), with summary cards for the period you choose.',
        start: 'Choose a date range and download — handy for bookkeeping or sharing a report with your team.',
      },
      models: {
        lede: 'The full catalog of AI models available through this system.',
        does: 'Browse models by type (chat / embedding / image / video / audio) along with the price for each.',
        start: 'Pick a model and click "Quick start" to copy a ready-to-run snippet (curl / python / node).',
      },
      topUp: {
        lede: (isB2B) =>
          isB2B
            ? "Where your organization's credit balance is managed — hidden from the left menu unless you're an admin."
            : "Where your account's credit balance is managed — hidden from the left menu unless you're an admin.",
        does: 'Add credit by card or PromptPay QR, attach a transfer slip if automatic payment has an issue, manage tax invoice details, and review past top-ups.',
        start: 'Click top-up, pick a payment method, and confirm — your balance updates in Overview as soon as payment succeeds.',
      },
      team: {
        lede: (isB2B) => (isB2B ? "Where you manage your organization's members." : 'Where you manage your team.'),
        does: "See the full roster, invite new members by email, resend an invite, or cancel one that's still pending.",
        start: 'Click "Invite member", enter a teammate\'s email, and the system sends the invite link automatically.',
      },
      playground: {
        lede: 'A chat sandbox for trying models directly in your browser — no code required.',
        does: 'Test a model in real time, see the exact credit deducted as you go, and get a "Request credit" button the moment quota or balance runs out.',
        start: 'Type a test message and send it to see the real output and cost before wiring it into your own code.',
      },
      profile: {
        lede: 'Your personal account settings — not in the left menu, reached from your avatar in the top right instead.',
        does: 'Edit your name, avatar, and phone number, change your password, enable 2FA (authenticator app / SMS / email OTP), get recovery codes, and review your account\'s security history.',
        start: 'Click your avatar, top right → "Edit profile". We recommend turning on 2FA to keep your account secure.',
      },
      notifications: {
        lede: 'Your full notification history — not in the left menu, reached from the bell icon in the top bar instead.',
        does: (isB2B) =>
          `See every notification ever sent to you (a suspended API key, a low balance, a used-up free quota, a top-up request, being added to ${isB2B ? 'an organization' : 'a team'}), filter by type, and jump to the related page or contact support from each one. Key-suspension alerts include a reactivate button so you can reinstate the key without leaving this page.`,
        start: 'Click the bell icon, top right, then "View all notifications" at the bottom of the dropdown.',
      },
    },
  },
};
